/**
 * Where pop-up notifications appear for this user on a computer: bottom-right
 * (the default, clear of every builder's header bar) or top-right (the old
 * corner). Phones always show toasts at the top; that rule lives in the
 * Toaster itself (see lib/toastPosition.ts).
 *
 * Reads `profiles.toast_position` and writes it through the self-only RPC
 * `set_toast_position`, the same shape as the product-updates preference.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DEFAULT_TOAST_POSITION, isToastPosition, type ToastPosition } from '@/lib/toastPosition';

export const TOAST_POSITION_QUERY_KEY = ['profile', 'toast-position'] as const;

type RpcClient = {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>;
};

export function useToastPosition() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = [...TOAST_POSITION_QUERY_KEY, user?.id];

  const query = useQuery({
    queryKey,
    enabled: !!user?.id,
    queryFn: async (): Promise<ToastPosition> => {
      const { data, error } = await supabase
        .from('profiles')
        .select('toast_position' as never)
        .eq('id', user!.id)
        .maybeSingle();
      if (error) throw error;
      const row = data as { toast_position?: unknown } | null;
      // A row without the column yet (pre-migration) reads as the default.
      return isToastPosition(row?.toast_position) ? row.toast_position : DEFAULT_TOAST_POSITION;
    },
  });

  const mutation = useMutation({
    mutationFn: async (position: ToastPosition) => {
      const { error } = await (supabase as unknown as RpcClient).rpc('set_toast_position', { p_position: position });
      if (error) throw new Error(error.message);
      return position;
    },
    onMutate: async (position) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<ToastPosition>(queryKey);
      qc.setQueryData(queryKey, position);
      return { previous };
    },
    onError: (err, _position, ctx) => {
      // No snapshot (the initial read had failed) means there is nothing to
      // restore; refetch so a rejected choice never stays applied.
      if (ctx?.previous === undefined) void qc.invalidateQueries({ queryKey });
      else qc.setQueryData(queryKey, ctx.previous);
      toast.error(err instanceof Error ? err.message : 'Could not save that preference');
    },
    onSuccess: (position) => {
      toast.success(position === 'top-right' ? 'Notifications now pop up top right' : 'Notifications now pop up bottom right');
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return {
    position: query.data ?? DEFAULT_TOAST_POSITION,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    setPosition: (position: ToastPosition) => mutation.mutate(position),
  };
}
